import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { getRequest, postRequest } from '../../requestUtils';
import ProjectLead from '../ProjectLead';
import ReviewAlert from './ReviewAlert';
import HowCloraWorksForEmployers from '../HowCloraWorksForEmployers';
import ThankYouForReview from '../ThankYouForReview';
import EmployerProjectCard from './EmployerProjectCard';
import ReferralTrigger from '../ReferralTrigger';
import climbingTheLadderImg from '../../../assets/images/climbing-the-ladder.png';
import referralPersonImg from '../../../assets/images/referral-person.jpg';
import { analyticsTrack } from '../../analyticsUtils';
import './EmployerProjectIndexContainer.scss';

const renderEmptyText = (status) => {
  const statuses = {
    draft: { title: 'drafts', description: 'A project is in draft when it is still being edited and has not been submitted for review yet.' },
    open: { title: 'open projects', description: 'A project is open after it has been submitted to Clora for review and/or still being actively worked on.' },
    closed: { title: 'closed projects', description: 'A project closes when the engagement with the consultant terminates and/or the need no longer exists.' },
  };

  const statusInfo = statuses[status];

  if (statusInfo) {
    return (
      <div>
        <h1>{`0 ${statusInfo.title}`}</h1>
        <div className="body-text-2 description">
          {statusInfo.description}
        </div>
      </div>
    );
  }

  return null;
};

const renderTab = (isActive, clickTab, statusText, count) => (
  <a
    role="button"
    tabIndex={0}
    className={`body-text-2 medium-weight tab-active-${isActive} d-flex text-nowrap`}
    onClick={clickTab}
  >
    {statusText.split(' ')[0]}
    &nbsp;
    <span className="d-none d-md-block">
      {statusText.split(' ')[1]}
    </span>
    &nbsp;
    (
    {count}
    )
  </a>
);

const renderStep = (icon, title, description, showVerticalLine) => (
  <div className="step">
    <div>
      <div className="circle-icon-container">
        <div className="g-column g-column--centered"><i className={`fa fa-${icon}`} /></div>
      </div>
      {showVerticalLine && <div className="vertical-dotted-line" />}
    </div>
    <div className="step-description">
      <div className="body-text-2 medium-weight">{title}</div>
      {description}
    </div>
  </div>
);

class EmployerProjectIndexContainer extends Component {
  static scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  constructor(props) {
    super(props);
    this.state = {
      status: props.status || 'open',
      showHowCloraWorks: props.showHowCloraWorks,
      showThankYouForReview: props.reviewed !== null,
      showDemo: false,
      showDemoContainer: true,
      questionableId: null,
      projects: props.projects,
    };
    this.requestDemo = this.requestDemo.bind(this);
    this.closeHowCloraWorks = this.closeHowCloraWorks.bind(this);
    this.closeThankYouForReview = this.closeThankYouForReview.bind(this);
    this.openDemo = this.openDemo.bind(this);
    this.closeDemo = this.closeDemo.bind(this);
    this.hideDemoContainer = this.hideDemoContainer.bind(this);
    this.clickTab = this.clickTab.bind(this);
    this.renderReviewCard = this.renderReviewCard.bind(this);
  }

  requestDemo() {
    const {
      requestDemoUrl, csrfToken, userId, questionnaireId,
    } = this.props;
    postRequest(
      requestDemoUrl,
      { survey: { user_id: userId, questionnaire_id: questionnaireId } },
      csrfToken,
    ).then((response) => {
      this.setState({ showDemo: true, questionableId: response.data.id });
    });
  }

  closeHowCloraWorks() {
    this.setState({ showHowCloraWorks: false });
    analyticsTrack('Closed How Clora Works for Employer modal', {
      location: 'Projects Index View',
    });
  }

  closeThankYouForReview() {
    this.setState({ showThankYouForReview: false });
  }

  openDemo() {
    this.requestDemo();
    this.setState({ showDemo: true, showHowCloraWorks: false });
  }

  closeDemo() {
    this.setState({ showDemo: false });
  }

  hideDemoContainer() {
    this.setState({ showDemoContainer: false });
  }

  clickTab(status) {
    getRequest('/projects', { status }).then((response) => {
      analyticsTrack('Clicked a project status tab', {
        status,
        location: 'Projects Index View',
      });
      this.setState({ projects: response.data, status });
    });
  }

  renderEmptyProjectTab(showRequestDemo) {
    const { addProjectPath } = this.props;
    const { status, showDemoContainer } = this.state;
    return (
      <div>
        <div className={`empty-project-tab box-shadow-1 ${status}`}>
          {renderEmptyText(status)}
          <div className="mt-3">
            <a
              href={addProjectPath}
              className="btn btn-primary bigger-button"
              role="button"
              data-status={status}
              id="add-project-empty"
            >
              Add Project
            </a>
          </div>
        </div>
        {showRequestDemo && showDemoContainer
          && (
          <div className="project-lead-container g-row g-row--between">
            <div className="g-column g-column--centered mr-30">
              <div className="text-center">
                <i className="fa fa-rocket ms-2" />
                Discover how we can help you find the right talent
              </div>
            </div>
            <a
              onClick={() => this.openDemo()}
              id="project-lead-request-demo"
              className="btn btn-primary-dark medium-weight"
              role="button"
              tabIndex={0}
            >
              Request Demo
            </a>
          </div>
          )}
      </div>
    );
  }

  renderReviewCard() {
    const { reviews } = this.props;

    const numberOfReviews = reviews.length;

    if (numberOfReviews > 0) {
      return (
        <ReviewAlert
          reviews={reviews}
          numberOfReviews={numberOfReviews}
        />
      );
    }
    return null;
  }

  render() {
    const {
      addProjectPath,
      featuredConsultantsPath,
      questionnaireQuestions,
      questionableType,
      projectLeadUrl,
      userFirstName,
      counts,
      csrfToken,
      referralPath,
      newReferralPath,
      authCodeUrl,
      shareOnLinkedinUrl,
      returnFromLinkedIn,
      authStatus,
      isConsultant,
      location,
      referredUserType,
      reviewed,
      showProjectEmploymentType,
    } = this.props;
    const {
      status,
      showThankYouForReview,
      projects,
      showDemo,
      showHowCloraWorks,
      questionableId,
    } = this.state;

    const reminderCount = (counts.proposals_need_attention > 0 ? 1 : 0)
    + (counts.draft > 0 ? 1 : 0) + (counts.open > 0 ? 1 : 0);

    return (
      <div className="style-guide employer-dashboard">
        <Modal
          dialogClassName="how-clora-works-for-employers"
          show={showHowCloraWorks}
          onHide={() => this.closeHowCloraWorks()}
        >
          <Modal.Header closeButton />
          <Modal.Body>
            <HowCloraWorksForEmployers
              userFirstName={userFirstName}
              addProjectPath={addProjectPath}
              featuredConsultantsPath={featuredConsultantsPath}
              showModal={showHowCloraWorks}
              showDemo={this.openDemo}
              handleCloseModal={() => this.closeHowCloraWorks()}
            />
          </Modal.Body>
        </Modal>
        <Modal
          dialogClassName="thank-you-for-review"
          show={showThankYouForReview}
          onHide={() => this.closeThankYouForReview()}
        >
          <Modal.Header closeButton />
          <Modal.Body>
            <ThankYouForReview
              userFirstName={reviewed}
              location="Thank You For Review Modal"
              referredUserType="employer"
              csrfToken={csrfToken}
              newReferralPath="/referrals"
            />
          </Modal.Body>
        </Modal>
        <Modal show={showDemo} onHide={() => this.closeDemo()}>
          <Modal.Header closeButton />
          <Modal.Body id="project-lead" className="style-guide">
            {questionableId && (
            <ProjectLead
              showDemo={showDemo}
              questionnaireQuestions={questionnaireQuestions}
              questionableId={questionableId}
              questionableType={questionableType}
              csrfToken={csrfToken}
              projectLeadUrl={projectLeadUrl}
              afterSubmit={this.hideDemoContainer}
            />
            )}
          </Modal.Body>
        </Modal>

        <div className="dashboard-header">
          <div className="g-row px-sm-0 px-md-3">
            <div className="g-flex-7">
              <div className="header">
                <h1 className="hero-header mb-2">My projects</h1>
                <div className="body-text-2">
                  Hi&nbsp;
                  {userFirstName}
                  , you can track your project status
                  here to meet your consultants within days.
                </div>
              </div>
              <div className="project-tabs d-flex">
                {renderTab(status === 'draft', () => this.clickTab('draft'), 'Drafts', counts.draft)}
                {renderTab(status === 'open', () => this.clickTab('open'), 'Open Projects', counts.open)}
                {renderTab(status === 'closed', () => this.clickTab('closed'), 'Closed Projects', counts.closed)}
              </div>
            </div>
            <div className="g-flex-1 d-none d-md-block" />
            <div className="g-flex-4 d-none d-md-block text-center">
              <img src={climbingTheLadderImg} alt="" width="186" height="113" />
            </div>
          </div>

        </div>
        <div className="dashboard-body-background">
          <div className="dashboard-body g-row mb-5 px-3 px-lg-0">
            <div className="g-flex-7">
              {
                counts[status] > 0
                  ? (
                    <div className="project-list">
                      <div className="cards mb-3">
                        {projects.map((project) => (
                          <EmployerProjectCard
                            key={`project-${project.resource_id}`}
                            project={project}
                            status={status}
                            showProjectEmploymentType={showProjectEmploymentType}
                          />
                        ))}
                      </div>
                      <div className="text-center">
                        <a href={addProjectPath} className="btn btn-primary">
                          <i className="fa fa-plus me-3" />
                          Add Project
                        </a>
                      </div>
                    </div>
                  )
                  : this.renderEmptyProjectTab(
                    counts.open === 0 && counts.draft === 0 && counts.closed === 0,
                  )
              }
            </div>
            <div className="g-flex-1" />
            <div className="g-flex-4 ">
              <div className="cards">
                {this.renderReviewCard()}
              </div>
              {reminderCount
                ? (
                  <div className="mb-4 aside">
                    <h2>Reminders</h2>
                    <div className={`reminders reminders-count-${reminderCount} body-text-2`}>
                      {counts.proposals_need_attention > 0
                      && (
                      <div className="reminder-group-1">
                        <div className="reminder reminder-1 candidates-count">
                          <div className="circle-icon-container">
                            <div className="g-column g-column--centered">
                              <i className="fa fa-users" />
                            </div>
                          </div>
                          <div className="count">{counts.proposals_need_attention}</div>
                          <div className="text">
                            candidate
                            {counts.proposals_need_attention > 1 ? 's' : ''}
                            {' '}
                            to review
                          </div>
                        </div>
                      </div>
                      )}
                      <div className="reminder-group-2">
                        {counts.draft > 0
                        && (
                        <div className="reminder reminder-2 draft-count">
                          <span className="count">{counts.draft}</span>
                          {' '}
                          draft
                          {counts.draft > 1 ? 's' : ''}
                          {' '}
                          to finalize
                        </div>
                        )}
                        {counts.open > 0
                        && (
                        <div className="reminder reminder-3 open-count">
                          <span className="count">{counts.open}</span>
                          {' '}
                          open project
                          {counts.open > 1 ? 's' : ''}
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
                : null}
              <div className="aside how-clora-works-for-employer">
                <h2 className="mb-3">How Clora Works</h2>
                <div className="steps">
                  {renderStep('plus-circle', 'Create a project', null, true)}
                  {renderStep('hourglass-half', 'Submit to Clora', 'Clora reviews your project', true)}
                  {renderStep('phone', 'Scoping call', 'Our scientific team will set up a 15 min call to qualify your project', true)}
                  {renderStep('filter', 'Wait for candidates', 'Clora vets the best consultants for your needs', true)}
                  {renderStep('users', 'Review candidates', 'Review their proposals and interview the ones you like', true)}
                  {renderStep('handshake', 'Hire the best candidate!', null, false)}
                </div>
              </div>
              <div className="aside referral">
                <img src={referralPersonImg} alt="scientist" className="mb-3" />
                <h2 className="mb-2">Invite hiring managers!</h2>
                <div className="body-text-2 mb-3">
                  Help other hiring managers to find top consultants within days. Earn $500 when
                  your referral hire their first consultant through Clora.
                </div>
                <ReferralTrigger
                  csrfToken={csrfToken}
                  referralPath={referralPath}
                  newReferralPath={newReferralPath}
                  authCodeUrl={authCodeUrl}
                  shareOnLinkedinUrl={shareOnLinkedinUrl}
                  returnFromLinkedIn={returnFromLinkedIn}
                  authStatus={authStatus}
                  isConsultant={isConsultant}
                  location={location}
                  referredUserType={referredUserType}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

EmployerProjectIndexContainer.propTypes = {
  projects: PropTypes.shape({}).isRequired,
  status: PropTypes.string,
  userFirstName: PropTypes.string.isRequired,
  counts: PropTypes.shape({}),
  csrfToken: PropTypes.string.isRequired,
  referralPath: PropTypes.string.isRequired,
  newReferralPath: PropTypes.string.isRequired,
  authCodeUrl: PropTypes.string,
  shareOnLinkedinUrl: PropTypes.string.isRequired,
  returnFromLinkedIn: PropTypes.bool,
  authStatus: PropTypes.number,
  isConsultant: PropTypes.bool,
  location: PropTypes.string.isRequired,
  referredUserType: PropTypes.string.isRequired,
  addProjectPath: PropTypes.string.isRequired,
  featuredConsultantsPath: PropTypes.string.isRequired,
  questionnaireQuestions: PropTypes.string.isRequired,
  questionableType: PropTypes.string.isRequired,
  projectLeadUrl: PropTypes.string.isRequired,
  requestDemoUrl: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  questionnaireId: PropTypes.string.isRequired,
  showHowCloraWorks: PropTypes.bool,
  reviews: PropTypes.arrayOf(PropTypes.shape({})),
  reviewed: PropTypes.string,
  showProjectEmploymentType: PropTypes.bool,
};

EmployerProjectIndexContainer.defaultProps = {
  status: 'open',
  returnFromLinkedIn: false,
  authCodeUrl: null,
  authStatus: null,
  isConsultant: true,
  showHowCloraWorks: false,
  reviewed: null,
  counts: {},
  showProjectEmploymentType: false,
};

export default EmployerProjectIndexContainer;
